import { Component } from '@theme/component';
import { closest, clamp, center, getVisibleElements, prefersReducedMotion } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';
import { Scroller } from '@theme/scrolling';
import { cycleFocus } from '@theme/focus';

const LABEL_PLACEHOLDER = '§§CL_LABEL§§';

/**
 * Collection links component
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} container
 * @property {HTMLElement[]} [images]
 * @property {HTMLElement[]} [links]
 * @property {import('slideshow').Slideshow} slideshow
 * @property {HTMLElement} [mobileChrome]
 * @property {HTMLElement} [selectionLive]
 * @property {HTMLAnchorElement} [shopCta]
 *
 * @extends {Component<Refs>}
 */
class CollectionLinks extends Component {
  requiredRefs = ['container'];

  /** @type {Scroller} */
  #scroll;

  /** @type {MediaQueryList} */
  #mql;

  /** @type {boolean} */
  #mobileChromeActive = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);

    this.#scroll = new Scroller(this.refs.container, { onScroll: this.#handleScroll });

    this.#mql = window.matchMedia('(max-width: 749px)');
    this.#mql.addEventListener('change', this.#syncMobileTabLayout);
    queueMicrotask(() => this.#syncMobileTabLayout());
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#mql?.removeEventListener('change', this.#syncMobileTabLayout);
    this.#removeMobileTabChrome();
    this.#scroll.destroy();
  }

  get links() {
    return this.refs.links || [];
  }

  get currentIndex() {
    return this.links.findIndex((link) => link.getAttribute('aria-current') === 'true');
  }

  /**
   * @returns {boolean}
   */
  #wantsMobileShopChrome() {
    return (
      this.getAttribute('mobile-layout') === 'overlay_tabs' &&
      this.refs.shopCta instanceof HTMLAnchorElement &&
      this.hasAttribute('shop-cta-template')
    );
  }

  /**
   * @returns {boolean}
   */
  #mobileTabLayoutActive() {
    return this.#wantsMobileShopChrome() && this.#mql.matches;
  }

  #syncMobileTabLayout = () => {
    if (this.#mobileTabLayoutActive()) {
      this.#applyMobileTabChrome();
    } else {
      this.#removeMobileTabChrome();
    }
  };

  #applyMobileTabChrome() {
    if (this.#mobileChromeActive || !this.#wantsMobileShopChrome()) return;

    const { container } = this.refs;
    if (!container) return;

    this.#mobileChromeActive = true;

    container.setAttribute('role', 'tablist');

    for (const li of container.querySelectorAll(':scope > li')) {
      li.setAttribute('role', 'presentation');
    }

    for (const link of this.links) {
      link.setAttribute('role', 'tab');
    }

    this.addEventListener('click', this.#handleMobileOverlayClick, true);
    container.addEventListener('keydown', this.#handleMobilePillKeydown, true);

    const start = this.currentIndex >= 0 ? this.currentIndex : 0;
    this.#updateSelectedLink(start);
    this.#updateMobileChrome(start);
    this.#initSwipeCarets();
  }

  #removeMobileTabChrome() {
    if (!this.#mobileChromeActive) return;

    const { container } = this.refs;
    this.removeEventListener('click', this.#handleMobileOverlayClick, true);

    if (container) {
      container.removeEventListener('keydown', this.#handleMobilePillKeydown, true);
      container.setAttribute('role', 'list');

      for (const li of container.querySelectorAll(':scope > li')) {
        li.removeAttribute('role');
      }
    }

    for (const link of this.links) {
      link.removeAttribute('role');
      link.removeAttribute('aria-selected');
      link.removeAttribute('tabindex');
    }

    this.#mobileChromeActive = false;
  }

  /**
   * Mobile overlay: tab pills only update the preview. Navigation is via the CTA or a firm tap on the image link.
   *
   * @param {number} index
   * @param {Event} event
   */
  #handleMobileOverlayNavigation(index, event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey) return;

    const link = this.links[index];
    if (!(link instanceof HTMLAnchorElement)) return;

    event.preventDefault();

    this.#updateSelectedLink(index);
    this.refs.slideshow?.select(index, event, { animate: !prefersReducedMotion() });
    this.#updateMobileChrome(index);
  }

  /**
   * @param {Element | null | undefined} slide
   * @returns {number}
   */
  #indexOfSlideshowSlide(slide) {
    const slideshow = this.refs.slideshow;
    if (!slideshow || !(slide instanceof Element)) return -1;

    const slides = slideshow.querySelectorAll('slideshow-slide');
    return Array.prototype.indexOf.call(slides, slide);
  }

  /**
   * @param {Event} event
   */
  #handleMobileOverlayClick = (event) => {
    if (!this.#mobileChromeActive) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const pill = target.closest('a.collection-links__link');
    if (pill instanceof HTMLAnchorElement && this.links.includes(pill)) {
      const index = this.links.indexOf(pill);
      this.#handleMobileOverlayNavigation(index, event);
      return;
    }

    const imageLink = target.closest('a.collection-links__image');
    if (
      imageLink instanceof HTMLAnchorElement &&
      this.refs.slideshow instanceof HTMLElement &&
      this.refs.slideshow.contains(imageLink)
    ) {
      const slide = imageLink.closest('slideshow-slide');
      const index = this.#indexOfSlideshowSlide(slide);
      if (index < 0) return;

      // Same href as the shop CTA for this slide (#updateMobileChrome); allow native navigation (incl. modifier keys).
      this.#updateSelectedLink(index);
      this.#updateMobileChrome(index);
      return;
    }
  };

  /**
   * @param {string} template
   * @param {Record<string, string>} values
   */
  #fillTemplate(template, values) {
    let out = template;
    for (const [key, val] of Object.entries(values)) {
      out = out.split(key).join(val);
    }
    return out;
  }

  /**
   * @param {HTMLAnchorElement} link
   */
  #linkLabel(link) {
    // Prefer the logical label text (exclude any product/collection count suffix).
    const labelEl = link.querySelector('.collection-index-link-row__label > span');
    if (labelEl instanceof HTMLElement) {
      return labelEl.textContent.replace(/\s+/g, ' ').trim();
    }

    return link.innerText.replace(/\s+/g, ' ').trim();
  }

  /**
   * @param {number} index
   */
  #updateMobileChrome(index) {
    if (!this.#mobileChromeActive) return;

    const { shopCta, selectionLive } = this.refs;
    const { links } = this;
    const link = links[index];
    if (!link || !(shopCta instanceof HTMLAnchorElement)) return;

    const label = this.#linkLabel(link);

    const shopTpl = this.getAttribute('shop-cta-template') || '';
    const liveTpl = this.getAttribute('selection-live-template') || '';

    shopCta.href = link.href;
    shopCta.textContent = this.#fillTemplate(shopTpl, { [LABEL_PLACEHOLDER]: label });

    if (selectionLive instanceof HTMLElement) {
      selectionLive.textContent = this.#fillTemplate(liveTpl, { [LABEL_PLACEHOLDER]: label });
    }
  }

  /**
   * @returns {string}
   */
  #swipeCaretsStorageKey() {
    const id = this.dataset.sectionId || 'default';
    return `collection-links-swipe-carets-${id}`;
  }

  #initSwipeCarets() {
    const dismissed = sessionStorage.getItem(this.#swipeCaretsStorageKey()) === '1';

    if (dismissed || this.links.length <= 1) {
      this.setAttribute('data-swipe-carets-dismissed', 'true');
    } else {
      this.removeAttribute('data-swipe-carets-dismissed');
    }
  }

  #dismissSwipeCarets() {
    sessionStorage.setItem(this.#swipeCaretsStorageKey(), '1');
    this.setAttribute('data-swipe-carets-dismissed', 'true');
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleMobilePillKeydown = (event) => {
    if (!this.#mobileChromeActive) return;

    const target = event.target;
    if (!(target instanceof HTMLAnchorElement)) return;

    const link = target.closest('.collection-links__link');
    if (!link || !this.links.includes(link)) return;

    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (event.metaKey || event.ctrlKey) return;

    event.preventDefault();

    const index = this.links.indexOf(link);
    this.#handleMobileOverlayNavigation(index, event);
  };

  /**
   * Public method to select a collection link
   *
   * @param {number} targetIndex
   * @param {PointerEvent} [event]
   */
  select(targetIndex, event) {
    this.#updateSelectedLink(targetIndex);
    this.refs.slideshow?.select(targetIndex, undefined, { animate: false });
    if (event) this.#revealImage(event);

    if (this.#mobileChromeActive) {
      this.#updateMobileChrome(clamp(targetIndex, 0, Math.max(this.links.length - 1, 0)));
    }
  }

  /**
   * Update the selected link
   *
   * @param {number} index
   */
  #updateSelectedLink(index) {
    const { links } = this;
    const selectedIndex = clamp(index, 0, Math.max(links.length - 1, 0));

    for (const [i, link] of links.entries()) {
      link.setAttribute('aria-current', Boolean(i === selectedIndex).toString());

      if (this.#mobileChromeActive) {
        link.setAttribute('aria-selected', Boolean(i === selectedIndex).toString());
        link.tabIndex = i === selectedIndex ? 0 : -1;
      }
    }
  }

  /**
   * Handle slideshow select event
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = async (event) => {
    if (!event.detail.userInitiated) return;

    const { index } = event.detail;
    if (index === this.currentIndex) return;

    const selectedLink = this.links[index];
    if (!selectedLink) return;

    this.#updateSelectedLink(index);

    this.#scroll.to(selectedLink);

    if (this.#mobileChromeActive) {
      const trigger = event.detail.trigger;
      if (trigger === 'select' || trigger === 'scroll' || trigger === 'drag') {
        // Hide the swipe carets after the user makes their first interaction.
        this.#dismissSwipeCarets();
      }

      this.#updateMobileChrome(index);
    }
  };

  /**
   * Cycle focus to the next or previous link
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    let modifier = 0;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        modifier = 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        modifier = -1;
        break;
    }

    if (!modifier) return;

    event.preventDefault();
    cycleFocus(this.links, modifier);
  }

  /**
   * Handle scroll event
   */
  #handleScroll = () => {
    if (
      this.getAttribute('mobile-layout') === 'overlay_tabs' &&
      window.matchMedia('(max-width: 749px)').matches
    ) {
      return;
    }

    const { links } = this;
    const { container } = this.refs;
    const visibleLinks = getVisibleElements(this, links, 0.1);

    if (visibleLinks.length === 0) return;
    const centers = visibleLinks.map((link) => center(link, 'x'));
    const referencePoint = center(container, 'x');
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleLink = visibleLinks[centers.indexOf(closestCenter)];

    if (!closestVisibleLink) return;

    const index = links.indexOf(closestVisibleLink);

    this.select(index);
  };

  /**
   * Clear all selections
   */
  clearSelections = () => {
    // Clear all selections when mouse leaves container
    const { links } = this;
    const { images } = this.refs;

    // Reset all links to unselected state (opacity will reset via CSS)
    for (const link of links) {
      link.setAttribute('aria-current', 'false');
      if (this.#mobileChromeActive) {
        link.setAttribute('aria-selected', 'false');
      }
    }

    // Hide any revealed images
    if (images) {
      for (const image of images) {
        image.removeAttribute('reveal');
      }
    }
  };

  /**
   * Reveal an image
   *
   * @param {Event} event
   */
  #revealImage(event) {
    if (!(event instanceof PointerEvent)) return;
    if (event.pointerType === 'touch') return;

    const { target } = event;
    if (!(target instanceof HTMLElement)) return;

    const { images } = this.refs;
    const index = this.links.indexOf(target);
    const selectedImage = images?.[index];

    if (!selectedImage) return;

    // Cache image dimensions to avoid repeated layout reads
    let cachedImageHeight = selectedImage.offsetHeight;
    let cachedImageWidth = selectedImage.offsetWidth;

    /** @type {number | null} */
    let rafId = null;

    /** @param {PointerEvent} event */
    const updateImagePosition = (event) => {
      // Throttle with requestAnimationFrame to avoid layout thrashing
      if (rafId !== null) return;

      rafId = requestAnimationFrame(() => {
        rafId = null;

        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const offset = 15;

        const wouldBeCutOff = event.clientY + cachedImageHeight + offset > viewportHeight;
        const yPos = wouldBeCutOff ? event.clientY - cachedImageHeight - offset : event.clientY + offset;

        const xPos = Math.min(Math.max(offset, event.clientX + offset), viewportWidth - cachedImageWidth - offset);

        selectedImage.style.setProperty('--x', `${xPos}px`);
        selectedImage.style.setProperty('--y', `${yPos}px`);
      });
    };

    const reset = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      selectedImage.removeAttribute('reveal');
      target.removeEventListener('mousemove', updateImagePosition);
    };

    updateImagePosition(event);

    for (const image of images) {
      if (image === selectedImage) {
        image.setAttribute('reveal', '');
      } else {
        image.removeAttribute('reveal');
      }
    }

    target.addEventListener('mousemove', updateImagePosition);
    target.addEventListener('mouseleave', reset, { once: true });
  }
}

if (!customElements.get('collection-links-component')) {
  customElements.define('collection-links-component', CollectionLinks);
}
